import { Div, H5, Icon, P, PlusIcon, lightTheme } from '@vgrubs/components';
import {
  withMediaKind,
  type AudienceGroup,
} from '../helpers/announcementAudience';
import { AnnouncementGroupCard } from './AnnouncementGroupCard';

type Props = {
  groups: AudienceGroup[];
  onPatch: (id: number, patch: Partial<AudienceGroup>) => void;
  onToggleChip: (id: number, index: number) => void;
  onAdd: () => void;
  onRemove: (id: number) => void;
  /** Group ids with a selected member that has no image or GIF yet. */
  groupsMissingMedia: Set<number>;
};

export const AnnouncementAudienceStep = ({
  groups,
  onPatch,
  onToggleChip,
  onAdd,
  onRemove,
  groupsMissingMedia,
}: Props) => (
  <Div $flex $column $gap="16px" $mt="20">
    <P $m="0" $fs="13px" $color={lightTheme.lightGray}>
      {groups.length
        ? 'Who sees this. Each group needs an image or a GIF, and shares it with every member. Tap members to include or exclude them.'
        : 'Nobody yet. Add a group, then tap the apps and platforms it covers - members in one group share an image or GIF and a minimum version.'}
    </P>

    {/* With groups on screen the add control moves to a heading row above
        them: at the bottom of a list of tall cards it was below the fold,
        which is the one place someone will not look for it. */}
    {!!groups.length && (
      <Div $flex $alignCenter $between>
        <H5 $mb="0">Groups</H5>
        <Icon $pointer onClick={onAdd}>
          <PlusIcon />
        </Icon>
      </Div>
    )}

    {groups.map((group) => (
      <AnnouncementGroupCard
        key={group.id}
        group={group}
        onName={(name) => onPatch(group.id, { name })}
        onMedia={(media, mediaKind) => onPatch(group.id, { media, mediaKind })}
        onMediaKind={(kind) => onPatch(group.id, withMediaKind(group, kind))}
        onMinVersion={(minAppVersion) => onPatch(group.id, { minAppVersion })}
        onToggleChip={(index) => onToggleChip(group.id, index)}
        onRemove={() => onRemove(group.id)}
        missingMedia={groupsMissingMedia.has(group.id)}
      />
    ))}

    {!groups.length && (
      <Div
        $flex
        $alignCenter
        $justifyCenter
        $gap="8px"
        $pointer
        onClick={onAdd}
      >
        <Icon>
          <PlusIcon />
        </Icon>
        <H5 $m="0" $color={lightTheme.primary}>
          Add a group
        </H5>
      </Div>
    )}
  </Div>
);
